import type { OpsPermission } from "../../shared/operations-schemas";

const AREAS = [
  "Customers",
  "Knowledge",
  "Billing",
  "Docs",
  "Support",
  "Audit",
  "Employees",
] as const;
type Area = (typeof AREAS)[number];

/** ADR-0087: every permission key in the words employees use. */
const ABILITIES: Record<OpsPermission, { area: Area; text: string }> = {
  "customer.read": { area: "Customers", text: "See customers" },
  "customer.create": { area: "Customers", text: "Add customers" },
  "customer.provision": { area: "Customers", text: "Set up a customer’s workspace" },
  "customer.retry": { area: "Customers", text: "Retry a setup step that failed" },
  "customer.address.manage": { area: "Customers", text: "Change a workspace address" },
  "organization.read": { area: "Customers", text: "See a customer’s people and sign-in settings" },
  "context.read": { area: "Knowledge", text: "Read notes, records, and tools" },
  "context.write": { area: "Knowledge", text: "Add and edit notes, records, and tools" },
  "context.search": { area: "Knowledge", text: "Search customer knowledge" },
  "artifact.read": { area: "Knowledge", text: "Open customer files" },
  "artifact.upload": { area: "Knowledge", text: "Upload files" },
  "billing.read": { area: "Billing", text: "See quotes, terms, invoices, and contracts" },
  "billing.manage": { area: "Billing", text: "Write quotes, terms, and contracts" },
  "billing.collect": { area: "Billing", text: "Charge customers and issue refunds" },
  "docs.read": { area: "Docs", text: "Read internal Docs" },
  "docs.write": { area: "Docs", text: "Write internal Docs" },
  "docs.manage": { area: "Docs", text: "Manage Docs spaces and who can see them" },
  "support.session.start": { area: "Support", text: "Ask a customer for support access" },
  "support.session.write": {
    area: "Support",
    text: "Make changes inside a customer’s tools during approved support access",
  },
  "support.ticket.read": { area: "Support", text: "Read support tickets" },
  "support.ticket.write": { area: "Support", text: "Work support tickets and write replies" },
  "support.automation.manage": {
    area: "Support",
    text: "Turn the support AI automations on or off and choose their model",
  },
  "audit.read": { area: "Audit", text: "Read audit trails" },
  "permissions.manage": { area: "Employees", text: "Add employees and change what they can do" },
};

/** Abilities grouped by area in a fixed order, for "What this person can do". */
export function abilitiesByArea(permissions: readonly OpsPermission[]) {
  const granted = new Set(permissions);
  return AREAS.map((area) => ({
    area,
    abilities: (Object.keys(ABILITIES) as OpsPermission[])
      .filter((permission) => granted.has(permission) && ABILITIES[permission].area === area)
      .map((permission) => ABILITIES[permission].text),
  })).filter((group) => group.abilities.length > 0);
}
